/**
 * Cost formatting utility
 * Formats cost values with configurable decimal places and always rounds up
 */

/**
 * Get the number of decimal places from environment variable
 * Defaults to 3 if not set or invalid
 */
const getDecimalPlaces = (): number => {
  const envValue = import.meta.env.VITE_COST_DECIMAL_PLACES;
  const parsed = parseInt(envValue, 10);
  return !isNaN(parsed) && parsed >= 0 ? parsed : 3;
};

/**
 * Format a cost value with the configured decimal places
 * Always rounds UP (ceiling) to avoid undercharging users
 *
 * @param cost - The cost value to format (number, string, null, or undefined)
 * @param options - Optional formatting options
 * @returns Formatted cost string with $ prefix (e.g., "$1.234")
 *
 * @example
 * // With VITE_COST_DECIMAL_PLACES=3
 * formatCost(1.2345) // "$1.235"
 * formatCost(0.000456) // "$0.001"
 * formatCost(0) // "$0.000"
 * formatCost(null) // "$0.000"
 */
export const formatCost = (
  cost: number | string | null | undefined,
  options?: {
    showCurrency?: boolean; // Default: true
    decimalPlaces?: number; // Override env variable
  }
): string => {
  const showCurrency = options?.showCurrency !== false;
  const decimalPlaces = options?.decimalPlaces ?? getDecimalPlaces();

  // Handle null/undefined/invalid values
  let numericCost: number;
  if (cost === null || cost === undefined) {
    numericCost = 0;
  } else if (typeof cost === 'string') {
    numericCost = parseFloat(cost);
    if (isNaN(numericCost)) {
      numericCost = 0;
    }
  } else {
    numericCost = cost;
  }

  // Handle negative costs (credits/refunds)
  const isNegative = numericCost < 0;
  const absoluteCost = Math.abs(numericCost);

  // Round UP using ceiling with the specified decimal places
  const multiplier = Math.pow(10, decimalPlaces);
  const roundedCost = Math.ceil(absoluteCost * multiplier) / multiplier;

  // Format with fixed decimal places
  const formattedValue = roundedCost.toFixed(decimalPlaces);

  // Add currency symbol and handle negative values
  const prefix = showCurrency ? '$' : '';
  return isNegative ? `-${prefix}${formattedValue}` : `${prefix}${formattedValue}`;
};

/**
 * Format cost without currency symbol
 * Useful for input fields or calculations display
 */
export const formatCostValue = (cost: number | string | null | undefined): string => {
  return formatCost(cost, { showCurrency: false });
};

/**
 * Format a "remaining/total" credit display
 * Example: "$1.234/$5.000"
 */
export const formatRemainingTotal = (
  remaining: number | string | null | undefined,
  total: number | string | null | undefined
): string => {
  return `${formatCost(remaining)}/${formatCost(total)}`;
};
